<template>
  <FilterBox>
    <div class="filter-box__header">
      <Button
        style-type="secondary"
        :disabled="!resourcesStore.hasActiveFilters"
        @click="clearFilters"
      >
        {{
          $textDictionary['pdp.filters.resetFiltersNone.label'] ||
          'Reset Filters'
        }}
      </Button>
    </div>
    <div class="filter-box__content">
      <AccordionComponent
        :key="componentKey"
        open-multiple-items
        :item-heading-level="4"
      >
        <!-- Display filters grouped by categories -->
        <AccordionItem
          v-for="(filterCategory, categoryIndex) in categorizedFilters"
          :key="`${filterCategory.name}_${categoryIndex}`"
          :is-open="categoryIndex < 2"
          :data-testid="`resourcelist_filter_${filterCategory.name}`"
        >
          <template #title>
            {{ getCategoryTitle(filterCategory.name) }}
          </template>
          <div>
            <!-- Search input field for categories with more than 8 options -->
            <div v-if="shouldShowSearch(filterCategory)" class="mb-3">
              <PrimeVueFloatLabel variant="on">
                <PrimeVueIconField>
                  <PrimeVueInputText
                    :id="`search_${filterCategory.name}`"
                    v-model="facetQueries[filterCategory.name]"
                    type="search"
                    name="search"
                    size="small"
                    suffix-icon="search"
                    :label="getSearchLabel(filterCategory.name)"
                    :disabled="resourcesStore.loading"
                    class="w-100 filter-search-input"
                    autocomplete="off"
                  />
                  <PrimeVueInputIcon>
                    <icon :name="'action_bar_magnifier'" />
                  </PrimeVueInputIcon>
                </PrimeVueIconField>
                <label :for="`search_${filterCategory.name}`">{{
                  getSearchLabel(filterCategory.name)
                }}</label>
              </PrimeVueFloatLabel>
            </div>

            <div
              v-for="(filterValue, valueIndex) in getFilteredValues(
                filterCategory
              )"
              :key="`${filterCategory.name}_${filterValue.value}_${valueIndex}`"
              class="mb-2"
            >
              <div class="d-flex flex-row align-items-center gap-2">
                <PrimeVueCheckbox
                  v-show="filterValue.count > 0"
                  :binary="true"
                  :model-value="isFilterActive(getFilterKey(filterValue.value))"
                  :input-id="`filter_${filterCategory.name}_${valueIndex}`"
                  :value="getFilterKey(filterValue.value)"
                  size="small"
                  @update:model-value="
                    () => handleFilterChange(getFilterKey(filterValue.value))
                  "
                />
                <label
                  v-show="filterValue.count > 0"
                  :for="`filter_${filterCategory.name}_${valueIndex}`"
                  class="p-label"
                >
                  {{ getFilterLabel(filterValue.value) }} ({{
                    filterValue.count
                  }})
                </label>
              </div>
            </div>

            <!-- Show more/Show less button -->
            <LinkElement
              v-if="shouldShowMoreButton(filterCategory)"
              :link="''"
              :title="getButtonText(filterCategory.name)"
              :target-blank="false"
              icon-type="icon-left"
              :icon-name="getChevronIcon(filterCategory.name)"
              class="mt-3"
              :disabled="resourcesStore.loading"
              @click="() => toggleShowMore(filterCategory.name)"
            >
              {{ getButtonText(filterCategory.name) }}
            </LinkElement>
          </div>
        </AccordionItem>
      </AccordionComponent>
    </div>
  </FilterBox>
</template>

<script setup lang="ts">
import type { SearchTagModel } from '~/utils/createDictKeysForSearchTags';
import {
  Button,
  FilterBox,
  AccordionComponent,
  AccordionItem,
  Icon,
  LinkElement
} from '@hypercodestudio/basler-components';
import { useResourcesStore } from '~/stores/globalSearch/resourcesStore';
import { debounce } from '~/composables/useDebounce';

interface Props {
  tags: SearchTagModel[];
  searchResponse: any;
}

interface FilterCategory {
  name: string;
  values: FilterValue[];
}

interface FilterValue {
  value: string;
  count: number;
}

const props = defineProps<Props>();
const { $textDictionary } = useNuxtApp();
const resourcesStore = useResourcesStore();
const route = useRoute();

// Search functionality
const optionsToDisplay = 8;
const facetQueries = ref<Record<string, string>>({});
const facetSearchResults = ref<Record<string, FilterValue[]>>({});
const showLessStates = ref<Record<string, boolean>>({});

/**
 * Check if search should be shown for a filter category
 */
function shouldShowSearch(filterCategory: FilterCategory): boolean {
  return filterCategory.values.length > optionsToDisplay;
}

/**
 * Get search label for a filter category
 */
function getSearchLabel(categoryName: string): string {
  const categoryTitle = getCategoryTitle(categoryName);
  return (
    $textDictionary.value['productsList.filter.search.placeholder']?.replace(
      '{category}',
      categoryTitle
    ) ?? `Search ${categoryTitle}`
  );
}

/**
 * Get filtered values for a filter category based on search query
 */
function getFilteredValues(filterCategory: FilterCategory): FilterValue[] {
  const query = facetQueries.value[filterCategory.name];

  if (!query || !query.trim()) {
    // No search query, apply show more/show less logic
    const showLess = showLessStates.value[filterCategory.name] ?? true;
    if (showLess && filterCategory.values.length > optionsToDisplay) {
      return filterCategory.values.slice(0, optionsToDisplay);
    }
    return filterCategory.values;
  }

  // Check if we have cached search results for this category
  const searchResults = facetSearchResults.value[filterCategory.name];
  if (searchResults) {
    return searchResults;
  }

  return filterCategory.values;
}

/**
 * Check if a category has more options than the display limit
 */
function hasMoreOptions(filterCategory: FilterCategory): boolean {
  return filterCategory.values.length > optionsToDisplay;
}

/**
 * Get the show more/show less button text for a category
 */
function getButtonText(categoryName: string): string {
  const showLess = showLessStates.value[categoryName] ?? true;
  return showLess
    ? $textDictionary.value['productsList.filters.button.showmore.label'] ||
        'Show more'
    : $textDictionary.value['productsList.filters.button.showless.label'] ||
        'Show less';
}

/**
 * Get the chevron icon name for show more/show less button
 */
function getChevronIcon(categoryName: string): string {
  const showLess = showLessStates.value[categoryName] ?? true;
  return showLess ? 'chevron-down' : 'chevron-up';
}

/**
 * Toggle show more/show less state for a category
 */
function toggleShowMore(categoryName: string): void {
  const currentState = showLessStates.value[categoryName] ?? true;
  showLessStates.value[categoryName] = !currentState;
}

/**
 * Check if show more/show less button should be displayed
 */
function shouldShowMoreButton(filterCategory: FilterCategory): boolean {
  const query = facetQueries.value[filterCategory.name];
  // Only show the button if there's no active search and there are more options than the limit
  return (!query || !query.trim()) && hasMoreOptions(filterCategory);
}

/**
 * Filter options based on search query
 */
function filterOptions(
  filterValues: FilterValue[],
  query: string
): FilterValue[] {
  if (!query.trim()) {
    return filterValues;
  }

  return filterValues.filter((filterValue) => {
    const label = getFilterLabel(filterValue.value);
    return label.toLowerCase().includes(query.toLowerCase());
  });
}

/**
 * Computed property to get categorized filters from the search response
 */
const categorizedFilters = computed<FilterCategory[]>(() => {
  if (!props.searchResponse?.filters) {
    return [];
  }

  return props.searchResponse.filters.map((filter: any) => ({
    name: filter.name,
    values: filter.values || []
  }));
});

/**
 * Get the title for a filter category
 */
function getCategoryTitle(categoryName: string): string {
  const key = `searchPage.resourcesFilter.${categoryName}.label`;
  return $textDictionary.value[key] || key;
  // const titleMap: Record<string, string> = {
  //   detailed_topics:
  //     $textDictionary.value['searchPage.resourcesFilter.detailedTopics.label'] ||
  //     'Detailed Topics',
  //   topics: $textDictionary.value['searchPage.resourcesFilter.topics.label'] || 'Topics',
  //   tags: $textDictionary.value['resourcesFilter.tags.label'] || 'Tags'
  // };
  //
  // return titleMap[categoryName] || categoryName;
}

/**
 * Extract the filter key from a filter value
 * For values like "AOI, Fehlererkennung & Qualitätskontrolle<?>aoi", extract "aoi"
 */
function getFilterKey(filterValue: string): string {
  // if (filterValue.includes('<?>')) {
  //   return filterValue.split('<?>')[1];
  // }
  return filterValue.replace('<?>', '##_filter_##');
}

/**
 * Get the display label for a filter value
 * For values like "AOI, Fehlererkennung & Qualitätskontrolle<?>aoi", extract "AOI, Fehlererkennung & Qualitätskontrolle"
 */
function getFilterLabel(filterValue: string): string {
  const keyName = `searchPage.resourcesFilter.categories.${filterValue}.label`;
  if (keyName in $textDictionary.value) {
    return $textDictionary.value[keyName];
  }
  if (filterValue.includes('<?>')) {
    return filterValue.split('<?>')[0];
  }
  return filterValue;
}

/**
 * Check if a filter is active by looking at both store and URL parameters
 */
function isFilterActive(filterKey: string): boolean {
  // First check the store
  if (resourcesStore.filters[filterKey]) {
    return true;
  }

  // If not in store but categorized filters are not loaded yet, check URL parameters
  if (categorizedFilters.value.length === 0 && route.query.resourceTags) {
    const resourceTags = route.query.resourceTags;
    const tags = Array.isArray(resourceTags)
      ? resourceTags.join(',').split(',')
      : resourceTags.split(',');
    return tags.includes(filterKey);
  }

  return false;
}

/**
 * Handle filter change by updating the resources store
 */
function handleFilterChange(filterKey: string): void {
  // Toggle the filter in the resources store
  resourcesStore.toggleFilter(filterKey);

  // Sync to query parameters
  resourcesStore.syncToQueryParams();
}

/**
 * Clear all selected filters
 */
function clearFilters() {
  // resetFilters already calls syncToQueryParams internally
  resourcesStore.resetFilters();
}

// Use a key to force component re-render when needed
const componentKey = ref(0);

// Initialize filters from URL on component mount
onMounted(() => {
  resourcesStore.updateFromQueryParams();
});

// Watch for changes in the resourcesStore filters to trigger re-render
watch(
  () => resourcesStore.filters,
  () => {
    // Force component re-render when filters change
    componentKey.value++;
  },
  { deep: true }
);

// Watch for changes in categorized filters to trigger re-render
watch(
  categorizedFilters,
  () => {
    // Force component re-render when categorized filters change
    componentKey.value++;
  },
  { deep: true }
);

// Watch for changes in search queries with debounce
watch(
  facetQueries,
  debounce((newQueries: Record<string, string>) => {
    // Process each category's search query
    Object.entries(newQueries).forEach(([categoryName, query]) => {
      if (!query.trim()) {
        // Clear search results if query is empty
        delete facetSearchResults.value[categoryName];
        return;
      }

      // Find the category in categorizedFilters
      const category = categorizedFilters.value.find(
        (cat) => cat.name === categoryName
      );
      if (category) {
        // Filter the options based on the search query
        facetSearchResults.value[categoryName] = filterOptions(
          category.values,
          query
        );
      }
    });
  }, 300),
  { deep: true }
);
</script>

<style scoped>
.filter-box__header {
  min-height: 41px;
}

.p-label {
  cursor: pointer;
  margin-bottom: 0;
}

/* Ensure proper spacing between search and filter items */
.mb-3 + div:first-of-type {
  margin-top: 0.5rem;
}
</style>
